import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, FileText, Eye, Pencil, ShareNetwork, WhatsappLogo, Copy, FilePdf, Upload, X, CookingPot, MagnifyingGlass, Camera, FileXls, Gear, Scales, Buildings, ArrowsClockwise, SortAscending } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import RecipeImportAI from "@/components/RecipeImportAI";
import ExportTemplateConfig from "@/components/ExportTemplateConfig";
import ExportSelectorDialog from "@/components/ExportSelectorDialog";
import { resolveImageUrl, formatCurrency, calcBakerPercent, NON_METRIC_UNITS } from "@/pages/sheetUtils";
import MolecularIntelligencePanel from "@/components/technical_sheets/MolecularIntelligencePanel";

export default function TechnicalSheetList(props) {
  const { t } = useLanguage();
  const { activeTab, assignToPraca, changeSortOrder, displaySheets, filterPraca, filterType, handleDelete, handleEdit, handleExportPDF, handleProduce, handlePromoteToCompany, handleShare, handleShareLink, handleShareWhatsApp, handleView, isTeamMember, pracas, refreshSheets, scopedSheets, setActiveTab, setFilterPraca, setFilterType, setSheetSearch, sheetSearch, sheets, sortOrder, user } = props;
  return (
    <>
      <Card className="border-zinc-200 shadow-sm rounded-sm">
        <CardHeader className="p-3 md:p-6">
          <div className="flex flex-col gap-3">
            {/* Tabs: Company / Personal / Inteligência Molecular */}
            <div className="flex border-b border-zinc-200 gap-0 -mb-1 overflow-x-auto">
              <button
                onClick={() => setActiveTab("company")}
                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === "company" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
                data-testid="tab-company-sheets"
              >
                {user?.business_name || "Empresa"}
              </button>
              {(user?.is_employee || user?.has_team) && (
                <button
                  onClick={() => setActiveTab("personal")}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === "personal" ? "border-violet-600 text-violet-700" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
                  data-testid="tab-personal-sheets"
                >
                  Fichas Pessoais
                </button>
              )}
              <button
                onClick={() => setActiveTab("molecular")}
                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === "molecular" ? "border-amber-500 text-amber-700" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
                data-testid="tab-molecular-drafts"
              >
                Inteligência Molecular
              </button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="text-base md:text-lg font-semibold tracking-tight text-zinc-900 shrink-0">
                {activeTab === "molecular" ? "Inteligência Molecular / Rascunhos Dinâmicos" : (user?.is_employee || user?.has_team) && activeTab === "personal" ? t("ts_personal") : t("ts_registered")}
              </CardTitle>
              {activeTab !== "molecular" && (
              <div className="flex items-center gap-2 flex-1 max-w-md">
                {pracas.length > 0 && (
                  <select 
                    value={filterPraca} 
                    onChange={(e) => setFilterPraca(e.target.value)} 
                    className="h-9 text-xs border border-zinc-200 rounded-md px-2 bg-white min-w-[120px]"
                    data-testid="filter-praca-select"
                  >
                    <option value="all">Todas Praças</option>
                    <option value="none">Sem Praça</option>
                    {pracas.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                )}
                <div className="relative flex-1">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    data-testid="sheet-search"
                    value={sheetSearch}
                    onChange={(e) => setSheetSearch(e.target.value)}
                    placeholder={t("ts_search_sheet")}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
              </div>
              )}
            </div>
            {activeTab !== "molecular" && (
            <div className="flex items-center gap-2 flex-wrap mt-3">
              <div className="flex items-center gap-1.5">
                <SortAscending size={15} className="text-zinc-400 shrink-0" />
                <select
                  value={sortOrder}
                  onChange={(e) => changeSortOrder(e.target.value)}
                  className="h-8 text-xs border border-zinc-200 rounded-md px-2 bg-white text-zinc-700"
                  data-testid="sort-sheets-select"
                >
                  <option value="recently_edited">{t("ts_sort_edited")}</option>
                  <option value="recently_opened">{t("ts_sort_opened")}</option>
                  <option value="recently_created">{t("ts_sort_created")}</option>
                  <option value="alpha_asc">{t("ts_sort_az")}</option>
                  <option value="alpha_desc">{t("ts_sort_za")}</option>
                </select>
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="h-8 text-xs border border-zinc-200 rounded-md px-2 bg-white text-zinc-700"
                data-testid="filter-type-select"
              >
                <option value="all">{t("ts_all_types")}</option>
                <option value="normal">{t("ts_normal_sheets")}</option>
                <option value="sub">{t("ts_subrecipes")}</option>
              </select>
              <span className="text-[11px] text-zinc-400 ml-auto" data-testid="sheets-count">
                {displaySheets.length} {displaySheets.length === 1 ? "ficha" : "fichas"}
              </span>
            </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-3 md:p-6 pt-0">
          {activeTab === "molecular" ? (
            <MolecularIntelligencePanel user={user} refreshSheets={refreshSheets} />
          ) : scopedSheets.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-zinc-300 mb-3" />
              <p className="text-zinc-500">{t("ts_no_sheets")}</p>
              <p className="text-sm text-zinc-400 mt-1">Crie sua primeira ficha técnica!</p>
            </div>
          ) : (
            <>
              {/* Mobile: Card view */}
              <div className="sm:hidden space-y-2">
                {displaySheets.map((sheet) => (
                  <div key={sheet.id} className="border border-zinc-200 rounded-lg p-3" data-testid={`sheet-row-${sheet.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      {sheet.foto_url && (
                        <img src={resolveImageUrl(sheet.foto_url)} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0 border border-zinc-100" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-zinc-900 truncate">{sheet.name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          {sheet.is_subrecipe && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">SUB</span>}
                          {sheet.status === "draft" && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold" data-testid={`draft-badge-${sheet.id}`}>Rascunho (Atlas Core)</span>}
                          {sheet.praca_name && <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-bold">{sheet.praca_name}</span>}
                        </div>
                      </div>
                      <button onClick={() => handleView(sheet)} className="text-zinc-500 p-1" data-testid={`view-sheet-${sheet.id}`}>
                        <Eye size={18} />
                      </button>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className="text-zinc-500">Rend: <span className="font-mono font-bold text-zinc-800">{sheet.yield_quantity} {sheet.yield_unit}</span></span>
                      <span className="text-zinc-500">Custo: <span className="font-mono font-bold text-zinc-800">{formatCurrency(sheet.total_cost)}</span></span>
                      <span className="text-zinc-500 ml-auto">Un: <span className="font-mono font-bold text-emerald-700">{formatCurrency(sheet.cost_per_unit)}</span></span>
                    </div>
                    <div className="flex items-center gap-1 mt-2 pt-2 border-t border-zinc-100 flex-wrap">
                      {pracas.length > 0 && (
                        <select value={sheet.praca_id || "none"} onChange={(e) => assignToPraca(sheet.id, e.target.value)}
                          className="h-7 text-[10px] border border-zinc-200 rounded px-1.5 bg-white mr-1" data-testid={`assign-praca-mobile-${sheet.id}`}>
                          <option value="none">Sem Praça</option>
                          {pracas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleEdit(sheet)} data-testid={`edit-sheet-${sheet.id}`}>
                        <Pencil size={14} className="mr-1" /> Editar
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleExportPDF(sheet)} data-testid={`pdf-sheet-${sheet.id}`}>
                        <FilePdf size={14} className="mr-1 text-red-600" /> PDF
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleShareWhatsApp(sheet)} data-testid={`whatsapp-sheet-${sheet.id}`}>
                        <WhatsappLogo size={14} className="mr-1 text-green-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleShareLink(sheet)} data-testid={`share-link-sheet-${sheet.id}`}>
                        <ShareNetwork size={14} className="mr-1 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleProduce(sheet)} data-testid={`produce-sheet-${sheet.id}`}>
                        <CookingPot size={14} className="mr-1 text-amber-700" />
                      </Button>
                      {isTeamMember && sheet.scope === "personal" && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handlePromoteToCompany(sheet)} data-testid={`promote-sheet-${sheet.id}`} title="Tornar ficha da empresa">
                          <Buildings size={14} className="mr-1 text-violet-700" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs ml-auto" onClick={() => handleDelete(sheet.id)} data-testid={`delete-sheet-${sheet.id}`}>
                        <Trash size={14} className="text-rose-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Table view */}
              <div className="hidden sm:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Produto</TableHead>
                  {pracas.length > 0 && <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Praça</TableHead>}
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Rendimento</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Custo Total</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Custo/Unidade</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displaySheets.map((sheet) => (
                  <TableRow key={sheet.id} className="table-row" data-testid={`sheet-row-${sheet.id}`}>
                    <TableCell className="font-medium text-zinc-900">
                      <div className="flex items-center gap-2">
                        {sheet.foto_url ? (
                          <img src={resolveImageUrl(sheet.foto_url)} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0 border border-zinc-100" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-zinc-100 flex items-center justify-center flex-shrink-0">
                            <FileText size={14} className="text-zinc-400" />
                          </div>
                        )}
                        <span>{sheet.name}</span>
                        {sheet.is_subrecipe && <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold align-middle">SUB</span>}
                        {sheet.status === "draft" && <span className="ml-1.5 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold align-middle" data-testid={`draft-badge-${sheet.id}`}>Rascunho (Atlas Core)</span>}
                      </div>
                    </TableCell>
                    {pracas.length > 0 && (
                      <TableCell>
                        <select value={sheet.praca_id || "none"} onChange={(e) => assignToPraca(sheet.id, e.target.value)}
                          className="h-7 text-[11px] border border-zinc-200 rounded px-1.5 bg-white w-full max-w-[130px]" data-testid={`assign-praca-${sheet.id}`}>
                          <option value="none">—</option>
                          {pracas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </TableCell>
                    )}
                    <TableCell className="text-zinc-700">
                      <span className="font-mono">{sheet.yield_quantity}</span> {sheet.yield_unit}
                    </TableCell>
                    <TableCell className="text-right font-mono number text-zinc-900">
                      {formatCurrency(sheet.total_cost)}
                    </TableCell>
                    <TableCell className="text-right font-mono number text-zinc-900">
                      {formatCurrency(sheet.cost_per_unit)}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center space-x-1">
                        <Button
                          data-testid={`view-sheet-${sheet.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(sheet)}
                          title="Visualizar"
                        >
                          <Eye size={16} className="text-zinc-600" />
                        </Button>
                        <Button
                          data-testid={`edit-sheet-${sheet.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(sheet)}
                          title="Editar"
                        >
                          <Pencil size={16} className="text-zinc-600" />
                        </Button>
                        <Button
                          data-testid={`pdf-sheet-${sheet.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExportPDF(sheet)}
                          title="Exportar PDF"
                        >
                          <FilePdf size={16} className="text-red-600" weight="fill" />
                        </Button>
                        <Button
                          data-testid={`whatsapp-sheet-${sheet.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleShareWhatsApp(sheet)}
                          title="Compartilhar no WhatsApp"
                        >
                          <WhatsappLogo size={16} className="text-green-600" weight="fill" />
                        </Button>
                        <Button
                          data-testid={`share-sheet-${sheet.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleShare(sheet)}
                          title="Compartilhar / Copiar"
                        >
                          <Copy size={16} className="text-blue-600" />
                        </Button>
                        {isTeamMember && sheet.scope === "personal" && (
                          <Button
                            data-testid={`promote-sheet-${sheet.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePromoteToCompany(sheet)}
                            title="Tornar ficha da empresa"
                          >
                            <Buildings size={16} className="text-violet-700" />
                          </Button>
                        )}
                        <Button
                          data-testid={`delete-sheet-${sheet.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(sheet.id)}
                          title="Deletar"
                        >
                          <Trash size={16} className="text-rose-600" />
                        </Button>
                        <Button
                          data-testid={`produce-sheet-${sheet.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleProduce(sheet)}
                          title="Produzir (descontar estoque)"
                          className="bg-amber-50 hover:bg-amber-100"
                        >
                          <CookingPot size={16} className="text-amber-700" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
